import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import { branchSlug } from '../api/branch';
import { apiFetch } from '../api/client';
import type { BranchList } from '../api/types';
import { useViewBranch } from '../view/ViewBranchContext';

export const BranchSelector = () => {
  const { viewBranch, setViewBranch, isReadOnly } = useViewBranch();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const { data, error, isPending } = useQuery({
    queryKey: ['branches'],
    queryFn: () => apiFetch<BranchList>('/api/branches'),
  });

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (rootRef.current?.contains(e.target as Node) === false) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // 表示中ブランチの slug。自分を見ているときは /api/branches の self から導出する。
  // self は data 取得まで分からないので、その間はプレースホルダーを出してラベルを空にしない。
  const currentSlug =
    viewBranch !== null ? branchSlug(viewBranch) : data !== undefined ? branchSlug(data.self) : '…';

  const select = (branch: string, self: boolean) => {
    setViewBranch(self ? null : branch);
    setOpen(false);
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="閲覧するブランチを選ぶ"
        aria-expanded={open}
        title={isReadOnly ? '他ユーザーのブランチを閲覧中 (read-only)' : '閲覧するブランチを選ぶ'}
        className={`flex h-8 items-center gap-1.5 rounded border px-2.5 text-xs ${
          isReadOnly
            ? 'border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100'
            : 'border-gray-200 bg-gray-50 text-gray-600 hover:border-gray-300 hover:bg-gray-100'
        }`}
      >
        <span aria-hidden="true">{isReadOnly ? '👁' : '🧑'}</span>
        <span className="max-w-[7rem] truncate font-medium">{currentSlug}</span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open && (
        <div className="absolute top-full right-0 z-40 mt-1 w-56 rounded border bg-white p-1 text-sm shadow-lg">
          <div className="px-2 py-1.5 text-[10px] tracking-wide text-gray-400 uppercase">
            目線を切り替える
          </div>
          {isPending && <div className="px-2 py-1.5 text-xs text-gray-500">読込中...</div>}
          {error !== null && (
            <div className="px-2 py-1.5 text-xs text-red-500">ブランチ一覧の取得に失敗しました</div>
          )}
          {data?.branches.map((b) => {
            const selected = b.self ? viewBranch === null : b.branch === viewBranch;
            return (
              <button
                key={b.branch}
                type="button"
                onClick={() => select(b.branch, b.self)}
                className={`flex w-full items-center justify-between rounded px-2 py-1.5 text-left hover:bg-gray-50 ${
                  selected ? 'font-semibold text-gray-900' : 'text-gray-700'
                }`}
              >
                <span className="truncate">
                  {b.slug}
                  {b.self && <span className="ml-1 text-xs text-gray-400">(自分)</span>}
                </span>
                {selected && <span aria-hidden="true">✓</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
